import { inject, Injectable } from '@angular/core';
import {
    ApplicationStatus,
    Metadata,
    Page,
    Season,
    SeasonStatus,
    SeasonWithUpdateBy,
    UserApplication
} from '@gbr-monorepo/interfaces';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import { produce } from 'immer';
import { catchError, EMPTY, filter, finalize, Observable, of, switchMap, tap } from 'rxjs';
import { UserApplicationItem } from '../../../shared/interfaces/ApplicationItem';
import { ApplicationFilter } from '../../components/season-details/applications-table/applications-filter/application-filter.model';
import { FinancialOverviewResponse } from '../../models/season-financial.model';
import { ApplicationService } from '../../services/application.service';
import { SeasonService } from '../../services/season.service';
import { CurrentSeasonContext, SeasonStateModel } from './season-state.model';
import { SeasonStateOperations } from './season-state.operations';
import {
    ActivateSeason,
    AutoSelectForPayout,
    CloseSeason,
    DeleteApplicationComment,
    DeleteSeason,
    LazyLoadApplications,
    LazyLoadSeasons,
    LoadApplications,
    LoadFinancialOverview,
    LoadSeason,
    LoadSeasons,
    ReleasePayout,
    ResetAutoSelectedApplications,
    ResetSeason,
    SetSeason,
    ToggleSelectedForPayout,
    UpdateApplicationComment
} from './season.action';

const PAGE_SIZE = 20;

const INITIAL_SEASON_CONTEXT: CurrentSeasonContext = {
    season: undefined,
    financialOverview: undefined,
    applications: [],
    applicationsMetadata: undefined,
    applicationsPageSize: PAGE_SIZE,
    applicationFilters: {} as ApplicationFilter,
    hasPayoutApplications: false,
    hasPayoutApplicationsChecked: false,
    isLoadingSeasonDetails: false,
    isLoadingApplications: false,
    isLoadingSeasonFinancialOverview: false
};

@State<SeasonStateModel>({
    name: 'season',
    defaults: {
        seasons: [],
        seasonsMetadata: undefined,
        isLoadingSeasons: true,
        currentSeasonContext: INITIAL_SEASON_CONTEXT
    }
})
@Injectable()
export class SeasonState {
    private readonly seasonService = inject(SeasonService);
    private readonly applicationService = inject(ApplicationService);

    @Selector()
    static getSeasons(state: SeasonStateModel): SeasonWithUpdateBy[] {
        return state.seasons;
    }

    @Selector()
    static getSeason(state: SeasonStateModel): Season {
        return state.currentSeasonContext.season;
    }

    @Selector()
    static getFinancialOverview(state: SeasonStateModel): FinancialOverviewResponse {
        return state.currentSeasonContext.financialOverview;
    }

    @Selector()
    static getApplications(state: SeasonStateModel): UserApplicationItem[] {
        return state.currentSeasonContext.applications.map(application => ({
            ...application,
            type: SeasonStateOperations.getApplicationType(application),
            fiscalHalfYear: SeasonStateOperations.getFiscalHalfYear(application)
        }));
    }

    @Selector()
    static getApplicationsPageSize(state: SeasonStateModel): number {
        return state.currentSeasonContext.applicationsPageSize;
    }

    @Selector()
    static getApplicationsMetadata(state: SeasonStateModel): Metadata {
        return state.currentSeasonContext.applicationsMetadata;
    }

    @Selector()
    static allSeasonsLoaded(state: SeasonStateModel): boolean {
        return !state.seasonsMetadata?.hasNextPage;
    }

    @Selector()
    static allApplicationsLoaded(state: SeasonStateModel): boolean {
        return !(state.currentSeasonContext.applicationsMetadata?.hasNextPage ?? true);
    }

    @Selector()
    static isLoadingSeasons(state: SeasonStateModel): boolean {
        return state.isLoadingSeasons;
    }

    @Selector()
    static isLoadingApplications(state: SeasonStateModel): boolean {
        return state.currentSeasonContext.isLoadingApplications;
    }

    @Selector()
    static isSeasonsInitalLoading(state: SeasonStateModel): boolean {
        return state.isLoadingSeasons && state.seasons.length <= 0;
    }

    @Selector()
    static isLoadingSeasonDetails(state: SeasonStateModel): boolean {
        return (
            state.currentSeasonContext.isLoadingSeasonDetails ||
            state.currentSeasonContext.isLoadingSeasonFinancialOverview
        );
    }

    @Selector()
    static hasPayoutApplications(state: SeasonStateModel): boolean {
        return state.currentSeasonContext.hasPayoutApplications;
    }

    @Action(SetSeason)
    setSeason(ctx: StateContext<SeasonStateModel>, action: SetSeason) {
        this.updateSeasonContext(ctx, { season: action.payload });
    }

    @Action(ResetSeason)
    resetSeason(ctx: StateContext<SeasonStateModel>): void {
        ctx.patchState({
            currentSeasonContext: INITIAL_SEASON_CONTEXT
        });
    }

    @Action(LoadSeason)
    loadSeason(ctx: StateContext<SeasonStateModel>, action: LoadSeason): Observable<Season> {
        ctx.patchState({
            currentSeasonContext: {
                ...INITIAL_SEASON_CONTEXT,
                isLoadingSeasonDetails: true
            }
        });

        return this.seasonService.getSeasonById(action.payload.seasonId).pipe(
            tap(season =>
                this.updateSeasonContext(ctx, {
                    season: {
                        ...season,
                        startDate: season.startDate ? new Date(season.startDate) : null,
                        endDate: season.endDate ? new Date(season.endDate) : null,
                        applicationDeadline: season.applicationDeadline ? new Date(season.applicationDeadline) : null
                    },
                    isLoadingSeasonDetails: false
                })
            ),
            catchError(() => {
                this.updateSeasonContext(ctx, { isLoadingSeasonDetails: false });
                return of(void 0);
            })
        );
    }

    @Action(LoadFinancialOverview)
    loadFinancialOverview(
        ctx: StateContext<SeasonStateModel>,
        action: LoadFinancialOverview
    ): Observable<FinancialOverviewResponse> {
        this.updateSeasonContext(ctx, { isLoadingSeasonFinancialOverview: true });

        return this.seasonService.getSeasonFinancialOverviewBySeasonId(action.payload.seasonId).pipe(
            tap(financialOverview =>
                this.updateSeasonContext(ctx, {
                    financialOverview,
                    isLoadingSeasonFinancialOverview: false
                })
            ),
            catchError(() => {
                this.updateSeasonContext(ctx, { isLoadingSeasonFinancialOverview: false });
                return of(void 0);
            })
        );
    }

    @Action(LoadApplications, { cancelUncompleted: true })
    loadApplications(ctx: StateContext<SeasonStateModel>, action: LoadApplications): Observable<Page<UserApplication>> {
        const payoutStates: ApplicationStatus[] = [ApplicationStatus.APPROVED, ApplicationStatus.DECLINED];

        this.updateSeasonContext(ctx, { isLoadingApplications: true });

        const {
            filters: { searchTerm, status, commentFilter, payoutPeriods },
            seasonId,
            pageSize
        } = action.payload;
        const { applicationsPageSize, hasPayoutApplicationsChecked } = ctx.getState().currentSeasonContext;

        return this.seasonService
            .getSeasonApplicationsById(
                seasonId,
                0,
                pageSize ?? applicationsPageSize,
                searchTerm,
                status ?? [],
                commentFilter,
                payoutPeriods
            )
            .pipe(
                tap(response => {
                    const hasPayoutInResponse = response.items.some(item => payoutStates.includes(item.status));

                    this.updateSeasonContext(ctx, {
                        applications: response.items.flat(),
                        applicationsPageSize: pageSize,
                        applicationsMetadata: response.metadata,
                        applicationFilters: action.payload.filters,
                        hasPayoutApplications: hasPayoutInResponse,
                        hasPayoutApplicationsChecked: hasPayoutInResponse || hasPayoutApplicationsChecked
                    });
                }),
                filter(() => !hasPayoutApplicationsChecked),
                filter(response => !response.items.some(item => payoutStates.includes(item.status))),
                switchMap(() =>
                    this.seasonService.getSeasonApplicationsById(seasonId, 0, 1, undefined, payoutStates).pipe(
                        tap(response => {
                            this.updateSeasonContext(ctx, {
                                hasPayoutApplications: response.metadata.itemCount > 0,
                                hasPayoutApplicationsChecked: true
                            });
                        })
                    )
                ),
                catchError(() => {
                    this.updateSeasonContext(ctx, { isLoadingApplications: false });
                    return of(void 0);
                }),
                finalize(() => {
                    this.updateSeasonContext(ctx, { isLoadingApplications: false });
                })
            );
    }

    @Action(LazyLoadApplications)
    lazyLoadApplications(
        ctx: StateContext<SeasonStateModel>,
        action: LazyLoadApplications
    ): Observable<Page<UserApplication>> {
        const { page, pageSize, seasonId } = action.payload;
        const {
            applicationsPageSize,
            applicationFilters: { searchTerm, status, commentFilter, payoutPeriods }
        } = ctx.getState().currentSeasonContext;

        this.updateSeasonContext(ctx, { isLoadingApplications: true });

        return this.seasonService
            .getSeasonApplicationsById(
                seasonId,
                page,
                pageSize ?? applicationsPageSize,
                searchTerm,
                status,
                commentFilter,
                payoutPeriods
            )
            .pipe(
                tap(result => {
                    this.updateSeasonContext(ctx, {
                        applications: result.items,
                        applicationsPageSize: pageSize,
                        applicationsMetadata: result.metadata
                    });
                }),
                catchError(() => {
                    this.updateSeasonContext(ctx, { isLoadingApplications: false });
                    return of(void 0);
                }),
                finalize(() => this.updateSeasonContext(ctx, { isLoadingApplications: false }))
            );
    }

    @Action(ToggleSelectedForPayout)
    toggleSelectedForPayout(ctx: StateContext<SeasonStateModel>, action: ToggleSelectedForPayout) {
        const application = action.payload.application;
        const isSelectedForPayout = application.isSelectedForPayout;
        const currentContext = ctx.getState().currentSeasonContext;
        const potentialPayout = application.potentialPayout ?? 0;
        const previousApplications = currentContext.applications;
        const previousFinancialOverview = currentContext.financialOverview;

        this.updateSeasonContext(ctx, {
            applications: currentContext.applications.map(app =>
                app.id === application.id ? { ...app, isSelectedForPayout: !isSelectedForPayout } : app
            ),
            financialOverview: currentContext.financialOverview
                ? {
                      ...currentContext.financialOverview,
                      selectedApplications: isSelectedForPayout
                          ? Math.max(currentContext.financialOverview.selectedApplications - 1, 0)
                          : currentContext.financialOverview.selectedApplications + 1,
                      availableBudget: isSelectedForPayout
                          ? currentContext.financialOverview.availableBudget + potentialPayout
                          : currentContext.financialOverview.availableBudget - potentialPayout
                  }
                : undefined
        });

        return (
            isSelectedForPayout
                ? this.applicationService.unselectPayout(application.id)
                : this.applicationService.selectPayout(application.id)
        ).pipe(
            catchError(() => {
                this.updateSeasonContext(ctx, {
                    applications: previousApplications,
                    financialOverview: previousFinancialOverview
                });
                return EMPTY;
            })
        );
    }

    @Action(AutoSelectForPayout)
    autoSelectForPayout(ctx: StateContext<SeasonStateModel>, action: AutoSelectForPayout) {
        const { applicationCutOffDate, percentage } = action.payload;

        return this.seasonService.autoSelectApplications(ctx.getState().currentSeasonContext.season.id, {
            applicationCutOffDate,
            percentage
        });
    }

    @Action(ResetAutoSelectedApplications)
    resetAutoSelectedApplications(ctx: StateContext<SeasonStateModel>) {
        const {
            applicationsPageSize,
            season: { id: seasonId }
        } = ctx.getState().currentSeasonContext;

        return this.seasonService.resetAutoSelectedApplications(seasonId).pipe(
            switchMap(() =>
                ctx.dispatch([
                    new LoadFinancialOverview({ seasonId }),
                    new LoadApplications({
                        seasonId,
                        pageSize: applicationsPageSize,
                        filters: {} as ApplicationFilter
                    })
                ])
            )
        );
    }

    @Action(ReleasePayout)
    releasePayout(ctx: StateContext<SeasonStateModel>, action: ReleasePayout) {
        const { payoutCutOffDate } = action.payload;

        return this.seasonService.releasePayout(ctx.getState().currentSeasonContext.season.id, payoutCutOffDate);
    }

    @Action(LoadSeasons)
    loadSeasons(ctx: StateContext<SeasonStateModel>): Observable<Page<SeasonWithUpdateBy>> {
        ctx.patchState({ isLoadingSeasons: true });

        return this.seasonService.getSeasons(0, PAGE_SIZE, '').pipe(
            tap(result =>
                ctx.setState(
                    produce(ctx.getState(), state => {
                        state.seasons = result.items;
                        state.seasonsMetadata = result.metadata;
                        state.isLoadingSeasons = false;
                    })
                )
            ),
            catchError(() => {
                ctx.patchState({ isLoadingSeasons: false });
                return of(void 0);
            })
        );
    }

    @Action(LazyLoadSeasons)
    lazyLoadSeasons(ctx: StateContext<SeasonStateModel>): Observable<Page<SeasonWithUpdateBy>> {
        ctx.patchState({ isLoadingSeasons: true });
        const page = Math.round(ctx.getState().seasons.length / PAGE_SIZE);

        return this.seasonService.getSeasons(page, PAGE_SIZE, undefined).pipe(
            tap(result => {
                const { seasons } = ctx.getState();

                ctx.setState(
                    produce(ctx.getState(), state => {
                        state.seasons = seasons.concat(result.items);
                        state.seasonsMetadata = result.metadata;
                        state.isLoadingSeasons = false;
                    })
                );
            }),
            catchError(() => {
                ctx.patchState({ isLoadingSeasons: false });
                return of(void 0);
            })
        );
    }

    @Action(ActivateSeason)
    activateSeason(ctx: StateContext<SeasonStateModel>, { seasonId }: ActivateSeason): Observable<void> {
        return this.seasonService.activateSeason(seasonId).pipe(
            tap(() =>
                ctx.setState(
                    patch({
                        seasons: updateItem(season => season.id === seasonId, patch({ status: SeasonStatus.ACTIVE }))
                    })
                )
            ),
            catchError(() => of(void 0))
        );
    }

    @Action(CloseSeason)
    closeSeason(ctx: StateContext<SeasonStateModel>, { seasonId }: CloseSeason): Observable<void> {
        return this.seasonService.closeSeason(seasonId).pipe(
            tap(() => {
                const state = ctx.getState();

                ctx.setState(
                    patch({
                        seasons: updateItem(season => season.id === seasonId, patch({ status: SeasonStatus.CLOSED }))
                    })
                );

                if (state.currentSeasonContext.season?.id === seasonId) {
                    this.updateSeasonContext(ctx, {
                        season: { ...state.currentSeasonContext.season, status: SeasonStatus.CLOSED }
                    });
                }
            }),
            catchError(() => of(void 0))
        );
    }

    @Action(DeleteSeason)
    deleteSeason(ctx: StateContext<SeasonStateModel>, { seasonId }: DeleteSeason): Observable<void> {
        return this.seasonService.deleteSeason(seasonId).pipe(
            tap(() =>
                ctx.setState(
                    patch({
                        seasons: removeItem(season => season.id === seasonId)
                    })
                )
            ),
            catchError(() => of(void 0))
        );
    }

    @Action(UpdateApplicationComment)
    updateApplicationComment(ctx: StateContext<SeasonStateModel>, action: UpdateApplicationComment) {
        const currentContext = ctx.getState().currentSeasonContext;

        this.updateSeasonContext(ctx, {
            applications: currentContext.applications.map(app =>
                app.id === action.payload.id ? { ...app, comment: action.payload.comment } : app
            )
        });

        return this.applicationService.updateComment(action.payload.id, action.payload.comment);
    }

    @Action(DeleteApplicationComment)
    deleteApplicationComment(ctx: StateContext<SeasonStateModel>, { payload }: DeleteApplicationComment) {
        return this.applicationService.deleteComment(payload.id).pipe(
            tap(() => {
                const currentContext = ctx.getState().currentSeasonContext;

                this.updateSeasonContext(ctx, {
                    applications: currentContext.applications.map(app =>
                        app.id === payload.id ? { ...app, comment: '' } : app
                    )
                });
            })
        );
    }

    private updateSeasonContext(ctx: StateContext<SeasonStateModel>, updates: Partial<CurrentSeasonContext>): void {
        ctx.patchState({
            currentSeasonContext: {
                ...ctx.getState().currentSeasonContext,
                ...updates
            }
        });
    }
}
